import React, { useState, useEffect, useMemo } from 'react';
import GitHubService, { GitHubFile } from '../../../../../shared/lib/githubService';
import SyntaxHighlighter from './SyntaxHighlighter';
import {
  fetchManifest,
  fetchArtifactFile,
  buildFileTree,
  getSandboxBaseUrl,
  FileTreeNode
} from '../services/sandboxArtifactService';
import { getProjectGitSettingsFromStorage } from '../../../../../shared/lib/storageUtils';

interface CodeViewerProps {
  // Git mode props
  owner?: string;
  repo?: string;
  branch?: string;
  // Sandbox mode props
  source?: 'git' | 'sandbox';
  userId?: string | null;
  projectId?: string | null;
  // Common props
  initialPath?: string;
  onError?: (error: string) => void;
  refreshTrigger?: number;
}

interface FileTreeItem {
  name: string;
  path: string;
  type: 'file' | 'dir';
  children?: FileTreeItem[];
}

const CodeViewer: React.FC<CodeViewerProps> = ({ 
  owner, 
  repo, 
  branch, 
  source = 'git',
  userId = null,
  projectId = null,
  initialPath = '',
  onError,
  refreshTrigger = 0
}) => {
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [fileTree, setFileTree] = useState<FileTreeItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  
  const baseUrl = useMemo(() => {
    if (source === 'sandbox') {
      return getSandboxBaseUrl(userId, projectId);
    }
    return null;
  }, [source, userId, projectId]);

  const projectGitToken = useMemo(() => {
    if (source !== 'git' || projectId == null) return undefined;
    const s = getProjectGitSettingsFromStorage(projectId);
    const t = s?.token?.trim();
    return t || undefined;
  }, [source, projectId, refreshTrigger]);

  // Load repository contents (Git mode)
  useEffect(() => {
    if (source === 'git' && owner && repo && branch) {
      loadContents(currentPath);
    }
  }, [currentPath, owner, repo, branch, source, projectGitToken]);

  // Load manifest and build tree (Sandbox mode)
  useEffect(() => {
    if (source === 'sandbox' && baseUrl) {
      loadManifestAndBuildTree();
    }
  }, [baseUrl, source, refreshTrigger]);

  // Handle refresh trigger
  useEffect(() => {
    if (refreshTrigger > 0) {
      if (source === 'git' && owner && repo && branch) {
        loadContents(currentPath);
      } else if (source === 'sandbox' && baseUrl) {
        loadManifestAndBuildTree();
      }
    }
  }, [refreshTrigger, projectGitToken]);

  const loadContents = async (path: string) => {
    if (!owner || !repo || !branch) return;
    setIsLoading(true);
    setError(null);
    
    try {
      const contents = await GitHubService.getContents(owner, repo, path, branch, projectGitToken);
      
      // Convert to tree structure
      const treeItems: FileTreeItem[] = contents.map(item => ({
        name: item.name,
        path: item.path,
        type: item.type,
        children: item.type === 'dir' ? [] : undefined
      }));
      
      setFileTree(treeItems);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load contents';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadManifestAndBuildTree = async () => {
    if (!baseUrl) return;

    setIsLoading(true);
    setError(null);
    setManifestError(null);

    try {
      // Check sessionStorage cache
      const manifestCacheKey = `sandbox_manifest_${baseUrl}`;
      let manifest = null;
      
      const cached = sessionStorage.getItem(manifestCacheKey);
      if (cached) {
        try {
          manifest = JSON.parse(cached);
        } catch (e) {
          // Invalid cache, ignore
        }
      }

      if (!manifest) {
        manifest = await fetchManifest(baseUrl);
        sessionStorage.setItem(manifestCacheKey, JSON.stringify(manifest));
      }

      // Show everything from the sandbox manifest (all entries under raw)
      const tree = buildFileTree(manifest.entries);
      
      // Convert FileTreeNode[] to FileTreeItem[]
      const convertTree = (nodes: FileTreeNode[]): FileTreeItem[] => {
        return nodes.map(node => ({
          name: node.name,
          path: node.path,
          type: node.type,
          children: node.children ? convertTree(node.children) : undefined
        }));
      };

      setFileTree(convertTree(tree));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load manifest';
      setManifestError(errorMessage);
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadFileContent = async (filePath: string) => {
    setIsLoadingFile(true);
    setError(null);
    
    try {
      let content: string;
      
      if (source === 'sandbox' && baseUrl) {
        content = await fetchArtifactFile(baseUrl, filePath);
      } else if (source === 'git' && owner != null && repo != null && branch != null) {
        content = await GitHubService.getRawFileContent(owner, repo, filePath, branch, projectGitToken);
      } else {
        throw new Error('Invalid configuration: missing required props for data source');
      }
      
      setFileContent(content);
      setSelectedFile(filePath);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load file content';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleFileClick = (file: FileTreeItem) => {
    if (file.type === 'file') {
      loadFileContent(file.path);
    } else {
      // Toggle directory expansion
      const newExpanded = new Set(expandedDirs);
      if (newExpanded.has(file.path)) {
        newExpanded.delete(file.path);
      } else {
        newExpanded.add(file.path);
      }
      setExpandedDirs(newExpanded);
      
      // Navigate to directory (only for Git mode with breadcrumb navigation)
      if (source === 'git') {
        setCurrentPath(file.path);
      }
    }
  };

  const handleBreadcrumbClick = (path: string) => {
    setCurrentPath(path);
  };

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'root', path: '' }];
    
    let currentPathStr = '';
    parts.forEach(part => {
      currentPathStr += `/${part}`;
      breadcrumbs.push({ name: part, path: currentPathStr });
    });
    
    return breadcrumbs;
  };

  const getFileIcon = (file: FileTreeItem) => {
    if (file.type === 'dir') {
      return (
        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      );
    }
    
    const ext = GitHubService.getFileExtension(file.name);
    const isCode = GitHubService.isCodeFile(file.name);
    
    if (isCode) {
      return (
        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  const renderFileTreeRecursive = (items: FileTreeItem[], level: number = 0): React.ReactNode[] => {
    return items.map((item) => {
      const isExpanded = expandedDirs.has(item.path);
      const isSelected = selectedFile === item.path;

      return (
        <div key={item.path} className="w-full">
          <button
            onClick={() => handleFileClick(item)}
            className={`w-full flex items-center px-3 py-2 text-sm hover:bg-white/5 rounded-lg transition-colors ${
              isSelected ? 'bg-purple-500/20 text-purple-300' : 'text-gray-300'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
          >
            {item.type === 'dir' && (
              <svg
                className={`w-3 h-3 mr-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {getFileIcon(item)}
            <span className="ml-2 truncate">{item.name}</span>
          </button>
          {item.type === 'dir' && isExpanded && item.children && (
            <div className="w-full">
              {renderFileTreeRecursive(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const renderCodeContent = () => {
    if (!selectedFile) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Select a file to view its content</p>
          </div>
        </div>
      );
    }

    const language = GitHubService.getLanguageFromExtension(selectedFile);
    
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center space-x-2">
            {getFileIcon({ name: selectedFile.split('/').pop() || '', path: selectedFile, type: 'file' })}
            <span className="text-sm font-medium text-white">{selectedFile.split('/').pop()}</span>
            <span className="text-xs text-gray-400">({language})</span>
          </div>
          {source === 'git' && owner && repo && branch && (
            <a
              href={`https://github.com/${owner}/${repo}/blob/${branch}/${selectedFile}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              View on GitHub
            </a>
          )}
        </div>
        <div className="flex-1 overflow-auto">
          {isLoadingFile ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
            </div>
          ) : (
            <SyntaxHighlighter
              code={fileContent}
              language={language}
              className="p-4 text-sm font-mono leading-relaxed"
            />
          )}
        </div>
      </div>
    );
  };

  if (manifestError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-400 mb-2 font-semibold">Failed to load manifest</p>
          <p className="text-sm text-gray-400 mb-4">{manifestError}</p>
          <button
            onClick={() => {
              if (baseUrl) {
                sessionStorage.removeItem(`sandbox_manifest_${baseUrl}`);
              }
              loadManifestAndBuildTree();
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (error && !manifestError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-400 mb-2">Error loading {source === 'sandbox' ? 'artifacts' : 'repository'}</p>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* File Tree Sidebar */}
      <div className="w-80 border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-sm font-medium text-white mb-2">
            {source === 'sandbox' ? 'Sandbox Artifacts' : 'Repository'}
          </h3>
          {source === 'sandbox' ? (
            <div className="text-xs text-gray-400 truncate">
              {baseUrl?.replace('https://', '').replace('.sandbox.vishmaker.com', '') || 'N/A'}
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-400">
                {owner}/{repo}
              </div>
              <div className="text-xs text-gray-500">
                Branch: {branch}
              </div>
            </>
          )}
        </div>
        
        {/* Breadcrumbs (Git mode only) */}
        {source === 'git' && (
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center space-x-1 text-xs">
              {getBreadcrumbs().map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && (
                    <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <button
                    onClick={() => handleBreadcrumbClick(crumb.path)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        
        {/* File List */}
        <div className="flex-1 overflow-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
            </div>
          ) : fileTree.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">
              {source === 'sandbox' ? 'No code files found in manifest' : 'No files found'}
            </div>
          ) : (
            <div className="space-y-1">
              {renderFileTreeRecursive(fileTree)}
            </div>
          )}
        </div>
      </div>
      
      {/* Code Content */}
      <div className="flex-1 bg-[#0A071B]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
          </div>
        ) : (
          renderCodeContent()
        )}
      </div>
    </div>
  );
};

export default CodeViewer; 